import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type ServiceStandardItem = LookupItem;
type PositionItem = LookupItem & {
  title?: string;
};
type ChecklistItem = LookupItem;
type QCStandardItem = LookupItem;
type KPIItem = LookupItem;

type ServiceStandardMappingFilterProps = {
  filters: Partial<{
    serviceStandardId?: string;
    positionId?: string;
    checklistId?: string;
    qcStandardId?: string;
    kpiId?: string;
    search?: string;
  }>;
  onFiltersChange: (
    filters: Partial<{
      serviceStandardId?: string;
      positionId?: string;
      checklistId?: string;
      qcStandardId?: string;
      kpiId?: string;
      search?: string;
    }>,
  ) => void;
};

export function ServiceStandardMappingFilter({
  filters,
  onFiltersChange,
}: ServiceStandardMappingFilterProps) {
  return (
    <div
      className="flex flex-wrap items-center gap-4 border-b px-4 py-3"
      data-tourid="mapping-filter"
    >
      <div className="flex flex-1 flex-wrap items-center gap-4">
        <InfiniteCombobox<ServiceStandardItem>
          queryKey={["service-standards", "filter"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/service-standard/lookup`, {
              search: args.search,
              pageParam: args.pageParam,
            })
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/service-standard/lookup`, id)
          }
          className="max-w-xs"
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={filters.serviceStandardId || ""}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              serviceStandardId: value || undefined,
            })
          }
          placeholder="ກອງມາດຕະຖານບໍລິການ..."
          clearable
        />

        <InfiniteCombobox<PositionItem>
          queryKey={["positions", "filter"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, {
              search: args.search,
              pageParam: args.pageParam,
            })
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
          }
          className="max-w-xs"
          getLabel={(item) => (item as PositionItem).title || item.name}
          getValue={(item) => item.id}
          value={filters.positionId || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, positionId: value || undefined })
          }
          placeholder="ກອງຕໍາແໜ່ງ..."
          clearable
        />

        <InfiniteCombobox<ChecklistItem>
          queryKey={["checklists", "filter"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/checklists/lookup`, {
              search: args.search,
              pageParam: args.pageParam,
            })
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/checklists/lookup`, id)
          }
          className="max-w-xs"
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={filters.checklistId || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, checklistId: value || undefined })
          }
          placeholder="ກອງ Checklist..."
          clearable
        />

        <InfiniteCombobox<QCStandardItem>
          queryKey={["qc-standards", "filter"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/qc/standards/lookup`, {
              search: args.search,
              pageParam: args.pageParam,
            })
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/qc/standards/lookup`, id)
          }
          className="max-w-xs"
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={filters.qcStandardId || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, qcStandardId: value || undefined })
          }
          placeholder="ກອງມາດຕະຖານ QC..."
          clearable
        />

        <InfiniteCombobox<KPIItem>
          queryKey={["kpis", "filter"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/kpis/lookup`, {
              search: args.search,
              pageParam: args.pageParam,
            })
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/kpis/lookup`, id)
          }
          className="max-w-xs"
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={filters.kpiId || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, kpiId: value || undefined })
          }
          placeholder="ກອງ KPI..."
          clearable
        />
      </div>
    </div>
  );
}
